/**
 * THE KIT'S OWN COMPOSITIONS: the 21 harvested roles, plus whatever this brand adds, plus what this
 * brand says each one is FOR.
 *
 * TWO ASKS, ONE FIELD, and that is the design. Guri, 2026-09-05: "I should have the ability to add more
 * compositions, like my own ones. Also, these compositions should have rules, for example, where we
 * should use cover, how the cover works." Those look like two features and they are one record: a
 * composition the kit knows about, carrying what it is for and when to reach for it. A CUSTOM role is
 * that record with nothing behind it; a BUILT-IN role is that record laid over `SLIDE_ROLES`, which
 * already carries a purpose and a whenToUse — measured off 61 pages of real decks — that a brand can now
 * override in its own words instead of inheriting ours.
 *
 * WHY NOT A SECOND LIST OF CUSTOM ROLES BESIDE THE 21. Because everything downstream is keyed by role id
 * and nothing downstream cares where the id came from: `profile.references` stores by role, the page
 * index assigns a PDF page by role, `slideRoleMatch` picks a role for a deck slide, the tiles are keyed
 * by role. A separate list would mean every one of those learning about two kinds of role. One resolved
 * list means none of them does.
 *
 * WHAT A CUSTOM ROLE DOES NOT HAVE IS SLOTS. The 21 carry measured word caps per slot, which is what
 * stops a writer padding a slide with furniture; a brand inventing "Pricing table" has measured nothing.
 * So a custom role's brief is written from its purpose and its rules instead, and `roleBrief` grows one
 * branch for that — see `slideRoles.ts`. Inventing slot caps on the user's behalf would be a measurement
 * we did not make, printed as a hard limit.
 */
import type { MoodboardProfile } from '../types/presentation'
import { SLIDE_ROLES, type SlideRole } from '../style/slideRoles'
import type { LayoutPattern } from '../style/styleRecord'

/** The layout families a custom composition may be built on. Same vocabulary the 21 are assigned from. */
export const ROLE_FAMILIES: LayoutPattern[] = [
  'stack',
  'split',
  'grid',
  'spectrum',
  'field',
  'orbit',
  'ledger',
  'single-number',
  'full-bleed',
]

/**
 * What the KIT says about one composition.
 *
 * Every field except `id` is optional on purpose: this record is an OVERLAY. A built-in role with a rule
 * and nothing else stores exactly that, and keeps our label, our purpose, our slots and our word caps.
 */
export interface KitRole {
  id: string
  /** Custom roles must have one. On a built-in it renames the tile. */
  label?: string
  /** Custom roles only; a built-in's geometry is fixed so a regenerated tile stays comparable. */
  family?: LayoutPattern
  /** What this composition is for, in the brand's words. Overrides `SlideRole.purpose`. */
  purpose?: string
  /** When to reach for it. Overrides `SlideRole.whenToUse`. */
  whenToUse?: string
  /**
   * USAGE rules — how this composition is meant to work in this brand ("the cover never carries a
   * subtitle", "always end the headline with a period"). Distinct from `KitReference.rules`, which are
   * the edit decisions recorded against the DOCUMENT currently in the slot: these outlive any document
   * and ride every future generation of this role, including a fresh one after a discard.
   */
  rules?: string[]
  /** True when the brand invented this composition. A built-in overlay never sets it. */
  custom?: true
}

type ProfileWithRoles = MoodboardProfile & { compositionRoles?: KitRole[] }

const BUILT_IN_IDS = new Set(SLIDE_ROLES.map((r) => r.id))
const text = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

/** Every built-in id, so a caller can tell an overlay from an invention without importing the roles. */
export function isBuiltInRole(id: string): boolean {
  return BUILT_IN_IDS.has(id)
}

/**
 * Defensive read, the same shape `readPageIndex` and `readReferences` take: this arrives as JSON off a
 * column written by more than one version of this code, and a malformed record answers nothing rather
 * than crashing the kit page. A record with no id, or a custom one with no label, is dropped on the way
 * in — both would draw a tile nobody could name.
 */
export function readKitRoles(profile: MoodboardProfile | null | undefined): KitRole[] {
  const raw = (profile as ProfileWithRoles | null | undefined)?.compositionRoles
  if (!Array.isArray(raw)) return []
  const out: KitRole[] = []
  const seen = new Set<string>()
  for (const entry of raw) {
    const e = entry as Partial<KitRole> | null
    const id = text(e?.id)
    if (!id || seen.has(id)) continue
    const custom = e?.custom === true && !BUILT_IN_IDS.has(id)
    const label = text(e?.label)
    if (custom && !label) continue
    seen.add(id)
    const family = ROLE_FAMILIES.includes(e?.family as LayoutPattern) ? (e?.family as LayoutPattern) : undefined
    const rules = Array.isArray(e?.rules)
      ? e!.rules!.filter((r): r is string => typeof r === 'string' && r.trim().length > 0).map((r) => r.trim())
      : []
    out.push({
      id,
      ...(label ? { label } : {}),
      ...(family ? { family } : {}),
      ...(text(e?.purpose) ? { purpose: text(e?.purpose) } : {}),
      ...(text(e?.whenToUse) ? { whenToUse: text(e?.whenToUse) } : {}),
      ...(rules.length ? { rules } : {}),
      ...(custom ? { custom: true as const } : {}),
    })
  }
  return out
}

/** Write the list back, or clear it when nothing is left. */
export function withKitRoles(profile: MoodboardProfile, roles: readonly KitRole[]): MoodboardProfile {
  const rest: ProfileWithRoles = { ...(profile as ProfileWithRoles) }
  delete rest.compositionRoles
  return roles.length > 0 ? ({ ...rest, compositionRoles: [...roles] } as MoodboardProfile) : (rest as MoodboardProfile)
}

/**
 * An id for a composition the user just named.
 *
 * Slugged from the label, never colliding with a built-in and never with another custom one, because the
 * whole store is keyed by this string: a second "Cover" landing on `cover` would silently take over the
 * real cover's tile, its stored document and its assigned template page.
 */
export function newRoleId(label: string, existing: readonly KitRole[]): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'composition'
  const taken = new Set([...BUILT_IN_IDS, ...existing.map((r) => r.id)])
  if (!taken.has(base)) return base
  for (let n = 2; n < 200; n += 1) {
    const next = `${base}-${n}`
    if (!taken.has(next)) return next
  }
  return `${base}-${existing.length + 1}`
}

/**
 * The composition library this kit actually has: the 21 with the brand's own words laid over them, then
 * the brand's own compositions, in the order they were added.
 *
 * A CUSTOM ROLE COMES BACK AS A `SlideRole` WITH NO SLOTS, and that is deliberate rather than lazy — see
 * the header. `roleBrief` reads the empty list and writes the brief from purpose and rules instead.
 */
export function resolveKitRoles(profile: MoodboardProfile | null | undefined): SlideRole[] {
  const overlay = readKitRoles(profile)
  const byId = new Map(overlay.map((r) => [r.id, r] as const))
  const builtIns = SLIDE_ROLES.map((role) => {
    const own = byId.get(role.id)
    if (!own) return role
    return {
      ...role,
      ...(own.label ? { label: own.label } : {}),
      ...(own.purpose ? { purpose: own.purpose } : {}),
      ...(own.whenToUse ? { whenToUse: own.whenToUse } : {}),
    }
  })
  const customs: SlideRole[] = overlay
    .filter((r) => r.custom && !BUILT_IN_IDS.has(r.id))
    .map((r) => ({
      id: r.id,
      label: r.label ?? r.id,
      purpose: r.purpose ?? 'A composition this brand uses.',
      whenToUse: r.whenToUse ?? 'Reach for it when the brand says to.',
      compositions: [r.family ?? 'stack'],
      slots: [],
    }))
  return [...builtIns, ...customs]
}

/** The usage rules for one composition, or none. Read by the generation prompt, not by the tile. */
export function roleRules(profile: MoodboardProfile | null | undefined, roleId: string): string[] {
  return readKitRoles(profile).find((r) => r.id === roleId)?.rules ?? []
}

/** How many usage rules ride one generation. Past this the prompt is arguing with itself. */
export const MAX_ROLE_RULES = 8

/**
 * The usage rules as the writer reads them, or empty.
 *
 * Its own block rather than folded into the role brief, so a reader of the prompt can tell what the
 * PRODUCT measured (the slots and their caps) apart from what this BRAND decided.
 */
export function roleRulesPrompt(rules: readonly string[]): string {
  const kept = rules.filter((r) => r.trim()).slice(0, MAX_ROLE_RULES)
  if (kept.length === 0) return ''
  return [
    'HOW THIS BRAND USES THIS COMPOSITION. These are the brand\'s own standing rules for it, and they',
    'outrank the general guidance above wherever the two disagree:',
    ...kept.map((r) => `- ${r.trim()}`),
  ].join('\n')
}

/** Add or replace one record, keeping the order stable so tiles do not jump on an edit. */
export function upsertKitRole(roles: readonly KitRole[], next: KitRole): KitRole[] {
  const at = roles.findIndex((r) => r.id === next.id)
  if (at === -1) return [...roles, next]
  const copy = [...roles]
  copy[at] = { ...copy[at], ...next }
  return copy
}

/**
 * Forget one composition.
 *
 * A BUILT-IN IS NEVER REMOVED, only its overlay is: the 21 are the product's library and a kit that
 * "deleted" its cover would still generate covers, with our words back and the brand's own quietly gone.
 * Deleting a custom one drops the record; its stored document is the caller's to clear.
 */
export function removeKitRole(roles: readonly KitRole[], id: string): KitRole[] {
  return roles.filter((r) => r.id !== id)
}
